import type { DocumentSubject } from 'features/knowledgeDocumentViewer/types/documentSubjects'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'

import {
  DEFAULT_TARGET_PAGE,
  NavigationTargetCandidate,
} from './navigationTargetResolver'

/**
 * Collapse whitespace (OCR reference text can wrap across lines) and trim.
 * Matching is done case-insensitively on this normalized form.
 */
export function normalizeReferenceText(text: string): string {
  return text.replace(/\s+/g, ' ').trim()
}

export type DocReferenceResolverInput = {
  /** The reference text as extracted (an OPC code like "PD-401" or a full drawing number). */
  referenceText: string
  /** The viewer's current document id — never a navigation target for its own references. */
  currentDocumentId: string
  /** Site documents to match against (already string-filtered server-side or the full list). */
  systemDocumentEntries: SystemDocumentEntry[]
}

/**
 * True when `drawingNumber` ends with `referenceText` at a segment boundary.
 * Off-page connectors print the sheet-local part of the drawing number
 * ("PD-401") while the title block carries the fully-qualified form
 * ("20017664-PD-401"), so an exact-suffix match preceded by a non-alphanumeric
 * separator is the join. The boundary check keeps "PD-401" from matching
 * "20017664-XPD-401".
 */
function isBoundarySuffixMatch(
  drawingNumber: string,
  referenceText: string
): boolean {
  if (!drawingNumber.endsWith(referenceText)) return false
  if (drawingNumber.length === referenceText.length) return true
  const boundaryChar =
    drawingNumber[drawingNumber.length - referenceText.length - 1]
  return !/[a-z0-9]/i.test(boundaryChar)
}

/**
 * The texts to attempt resolution with for a clicked doc-reference box, in
 * order. A doc-reference subject often pairs an OPC code (`canonical_tag`
 * alias "PD-400") with the flag's descriptive label (`description` alias
 * "TO POTABLE WATER TEMPERING SKID") — clicking the label should still
 * navigate via the code, so canonical-tag aliases come first, then the
 * clicked text, then any remaining aliases.
 */
export function docReferenceResolutionTexts(
  subject: Pick<DocumentSubject, 'aliases'> | undefined,
  clickedLabel: string
): string[] {
  const ordered = [
    ...(subject?.aliases ?? [])
      .filter((alias) => alias.kind === 'canonical_tag')
      .map((alias) => alias.value),
    clickedLabel,
    ...(subject?.aliases ?? []).map((alias) => alias.value),
  ]
  const seen = new Set<string>()
  const texts: string[] = []
  for (const raw of ordered) {
    const text = normalizeReferenceText(raw)
    if (!text || seen.has(text.toLowerCase())) continue
    seen.add(text.toLowerCase())
    texts.push(text)
  }
  return texts
}

/**
 * True when `referenceText` is the current document's own drawing number —
 * exact or at the OPC segment boundary. Clicking a sheet's own title-block
 * print is an identity, not a failed lookup, so callers can acknowledge it
 * instead of reporting "no document found".
 */
export function isSelfReference(
  referenceText: string,
  currentDrawingNumber: string | null | undefined
): boolean {
  const needle = normalizeReferenceText(referenceText).toLowerCase()
  const drawingNumber = normalizeReferenceText(
    currentDrawingNumber ?? ''
  ).toLowerCase()
  if (!needle || !drawingNumber) return false
  return (
    drawingNumber === needle || isBoundarySuffixMatch(drawingNumber, needle)
  )
}

function toCandidate(document: SystemDocumentEntry): NavigationTargetCandidate {
  return {
    documentId: document.entry.id,
    // Document-level match only: string matching can't narrow to a sheet.
    pageNumber: DEFAULT_TARGET_PAGE,
    revisionCode: document.entry.data.revision_code,
  }
}

/**
 * Resolve a doc-reference subject's text to its target document(s), best tier wins:
 *
 * 1. exact `drawing_number` match — clicking a title-block print elsewhere
 * 2. boundary-suffix `drawing_number` match — the OPC → title-block join
 *    (fusion does NOT cluster an OPC with its target sheet: "PD-401" and
 *    "20017664-PD-401" normalize to different alias norms, so the subject's
 *    `documents` array only lists the sheets *bearing* the reference)
 * 3. `title` containment — text OPCs that point at a drawing by name
 *
 * The current document is excluded throughout (a sheet's own title block
 * resolves to itself). Empty result ⇒ the reference has no navigable target.
 */
export function resolveDocReferenceTargets({
  referenceText,
  currentDocumentId,
  systemDocumentEntries,
}: DocReferenceResolverInput): NavigationTargetCandidate[] {
  const needle = normalizeReferenceText(referenceText).toLowerCase()
  if (!needle) return []

  const candidates = systemDocumentEntries.filter(
    (document) => document.entry.id !== currentDocumentId
  )

  const exactMatches: SystemDocumentEntry[] = []
  const suffixMatches: SystemDocumentEntry[] = []
  const titleMatches: SystemDocumentEntry[] = []

  for (const document of candidates) {
    const drawingNumber = normalizeReferenceText(
      document.entry.data.drawing_number ?? ''
    ).toLowerCase()
    // `title` is often the source filename ("<number>.dwg.pdf"); the human
    // name lives in `document_title` — text OPCs only ever match the latter.
    const title = normalizeReferenceText(
      document.entry.data.title ?? ''
    ).toLowerCase()
    const documentTitle = normalizeReferenceText(
      document.entry.data.document_title ?? ''
    ).toLowerCase()

    if (drawingNumber && drawingNumber === needle) {
      exactMatches.push(document)
    } else if (drawingNumber && isBoundarySuffixMatch(drawingNumber, needle)) {
      suffixMatches.push(document)
    } else if (
      (title && title.includes(needle)) ||
      (documentTitle && documentTitle.includes(needle))
    ) {
      titleMatches.push(document)
    }
  }

  const best =
    exactMatches.length > 0
      ? exactMatches
      : suffixMatches.length > 0
        ? suffixMatches
        : titleMatches

  return best.map(toCandidate)
}
